import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Modal, View } from 'react-native';
import FormActions from '../../../components/FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedEquipmentId: number;
    setUnit: Dispatch<SetStateAction<AppAgentUnit | undefined>>;
    unit: AppAgentUnit;
    onConfirm: () => void;
}

export default function EditEquipmentModal({ selectedEquipmentId, isModalVisible, setModalVisible, unit, setUnit, onConfirm }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const selectedEquipment = useMemo(() => {
        return unit.equipments.find(equipment => equipment.id === selectedEquipmentId);
    }, [unit, selectedEquipmentId]);

    const rollbackUnit = useMemo(() => {
        return unit;
    }, [isModalVisible]);

    const [equipmentActive, setEquipmentActive] = useState<boolean>(false);
    const [reason, setReason] = useState<string>('');

    useEffect(() => {
        if (isModalVisible && selectedEquipment) {
            setEquipmentActive(selectedEquipment.active);
            setReason(selectedEquipment.reason || '');
        }
    }, [isModalVisible, selectedEquipment, unit]);

    const closeModal = () => {
        setModalVisible(false);
        setReason('');
        onConfirm();
    };

    if (!selectedEquipment) {
        return <></>;
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => setModalVisible(false)}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('equipmentRemoval')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                    </View>
                    <View style={{ flexDirection: 'column', rowGap: 16 }}>
                        <View style={{ flexDirection: 'column', rowGap: 8 }}>
                            <MyAppText style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primaryButton.background, textTransform: 'uppercase' }}>{translate('selectedEquipment')}</MyAppText>

                            <View>
                                <MyAppText style={styles.equipmentName}>{translate('equipmentName')}</MyAppText>
                                <MyAppText style={styles.equipmentInfo}>{selectedEquipment.name}</MyAppText>
                            </View>
                        </View>
                        <FormInput
                            labelStyle={styles.inputLabelStyle}
                            onChangeText={(value) => {
                                setReason(value);
                                setUnit(prevUnit => ({
                                    ...prevUnit!,
                                    equipments: prevUnit?.equipments.map(equipment =>
                                        equipment.id === selectedEquipmentId
                                            ? {
                                                ...equipment,
                                                reason: value
                                            }
                                            : equipment
                                    ) ?? []
                                }));
                            }}
                            value={reason}
                            multiline={true}
                            numberOfLines={4}
                            label={translate('deleteReason')}
                            disabled={!equipmentActive}
                        />
                    </View>
                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        onSubmit={closeModal}
                        onClose={() => {
                            setModalVisible(false);
                            setReason('');
                            setUnit(rollbackUnit);
                        }}
                        disabled={selectedEquipment.active && !selectedEquipment.reason}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        width: '100%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    equipmentName: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.modal.header,
        padding: 12,
        width: '100%',
        backgroundColor: theme.colors.paginationBar.background,
        borderTopRightRadius: 4,
        borderTopLeftRadius: 4,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.colors.container.border,
    },
    equipmentInfo: {
        fontSize: 14,
        fontWeight: '400',
        color: theme.colors.modal.subheader,
        padding: 12,
        width: '100%',
        borderBottomRightRadius: 4,
        borderBottomLeftRadius: 4,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.colors.container.border,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
    actionButton: (color: string) => ({
        flex: 1,
        borderWidth: 1,
        borderRadius: 8,
        borderColor: color,
        flexDirection: 'row',
        gap: 10,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 10
    }),
    actionLabel: (color: string) => ({
        fontWeight: 'bold',
        fontSize: 16,
        color: color
    })
}));
